// ./src/contexts/TaxSettingsContext/TaxSettingsContext.tsx
//
// The account's tax rates, shared by the Taxes page and its drawer.
//
// ⚠ PAGE-MOUNTED, not app-wide. Unlike tags or categories — which every picker on every page needs
// — tax rates are read by exactly two pickers, and both of those load their own copy through
// `TaxSelect`. Hoisting this to `App.tsx` would fetch the catalog for every host on every page to
// serve one screen.
import React, { createContext, useContext } from "react";

import { useTaxSettings, UseTaxSettingsReturn, UseTaxSettingsOptions } from "@/hooks/useTaxSettings";

const TaxSettingsContext = createContext<UseTaxSettingsReturn | null>(null);

export interface TaxSettingsProviderProps {
  children: React.ReactNode;
  options?: UseTaxSettingsOptions;
}

export const TaxSettingsProvider: React.FC<TaxSettingsProviderProps> = ({ children, options }) => {
  const value = useTaxSettings(options ?? {});

  return <TaxSettingsContext.Provider value={value}>{children}</TaxSettingsContext.Provider>;
};

/** Throws when used outside the provider — for components that cannot work without it. */
export const useTaxSettingsContext = (): UseTaxSettingsReturn => {
  const ctx = useContext(TaxSettingsContext);

  if (!ctx) {
    throw new Error("useTaxSettingsContext must be used within a TaxSettingsProvider");
  }

  return ctx;
};

/** Null outside the provider — for components that can fall back to their own props. */
export const useOptionalTaxSettingsContext = (): UseTaxSettingsReturn | null => useContext(TaxSettingsContext);
